import isUserLoggedIn from "@Middleware/isUserLoggedIn";
import isUserVerifiedOrSubscribed from "@Middleware/isUserVerifiedOrSubscribed";
import teamsAllowed from "@Middleware/teamsAllowed";

import { IS_BILLING_PAGE_ENABLED } from "@Constants/env";

const AppSettingsPage = () => import(/* webpackChunkName: 'dashboard'*/ "@Components/layouts/app/settings/index.vue");

const AppSettingsMenu = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/SettingsMenu.vue");
const AppSettingsProfile = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/profile/index.vue");
const AppSettingsProfileDeactivateAccountConfirm = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/profile/DeactivateAccountConfirm.vue");
const AppSettingsSecurity = () => import(/* webpackChunkName: 'settings' */ "@Views/settings/security/Index.vue");
const AppSettingsAPI = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/api/index.vue");
const AppSettingsAPIEdit = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/api/edit.vue");
const AppSettingsAPINew = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/api/new.vue");
const AppSettingsBrandedDomains = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/brandedDomains/index.vue");
const AppSettingsPixelScripts = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/pixelScripts/index.vue");
const AppSettingsUsage = () => import(/* webpackChunkName: 'settings' */ "@Views/settings/usage/Index.vue");
const AppSettingsInvoices = () => import(/* webpackChunkName: 'settings' */ "@Views/settings/invoices/Index.vue");
const AppSettingsSubscription = () => import(/* webpackChunkName: 'settings' */ "@Views/settings/subscriptions/Index.vue");
const AppSettingsSubscriptionSuccess = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/subscription/success.vue");
const AppSettingsSubscriptionCancel = () => import(/* webpackChunkName: 'settings' */ "@Views/pricing/ConfirmCancellation.vue");
const AppSettingsSubscriptionUpdateVerification = () => import(/* webpackChunkName: 'settings' */ "@Views/settings/subscriptions/UpdateVerification.vue");
const AppSettingsPaymentMethod = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/paymentMethod/index.vue");
const AppSettingsTeams = () => import(/* webpackChunkName: 'settings' */ "@Components/layouts/app/settings/teams/index.vue");
const AppSettingsBilling = () => import(/* webpackChunkName: 'settings' */ "@Modules/billing/Controllers/BillingSettingsController.vue");
const AppSettingsBillingTransactions = () => import(/* webpackChunkName: 'settings' */ "@Modules/billing/Controllers/BillingTransactionsController.vue");

const CreateApex = () => import(/* webpackChunkName: 'brandedDomains' */ "@Components/layouts/app/settings/brandedDomains/apex-domain-create-pop.vue");
const CreateSubdomain = () => import(/* webpackChunkName: 'brandedDomains' */ "@Components/layouts/app/settings/brandedDomains/sub-domain-create-pop.vue");
const CreateTiny = () => import(/* webpackChunkName: 'brandedDomains' */ "@Components/layouts/app/settings/brandedDomains/tiny-domain-create-pop.vue");
const AppSettingsBrandedDomainsEmailVerify = () => import(/* webpackChunkName: 'brandedDomains' */ "@Components/layouts/app/settings/brandedDomains/email-verify.vue");
const AppSettingsBrandedDomainsEdit = () => import(/* webpackChunkName: 'brandedDomains' */ "@Components/layouts/app/settings/brandedDomains/edit-pop.vue");

const AppShoppingCartOrders = () => import(/* webpackChunkName: 'settings' */ "@Components/cart/Orders.vue");
const AppShoppingCartOrder = () => import(/* webpackChunkName: 'shopping_cart' */ "@Components/cart/Order.vue");
const AppShoppingCartOrderPurchasedNotification = () => import(/* webpackChunkName: 'shopping_cart' */ "@Components/cart/OrderPurchasedNotification.vue");

// When enabled, the legacy invoices and payment method pages hand off to the billing and
// payment page. Spread onto the route record so navigation by name redirects too — a
// path-only redirect is skipped when the router resolves a target by name.
const billingPageRedirect = IS_BILLING_PAGE_ENABLED === "true"
  ? { redirect: { name: "settings.billing" } }
  : {};

export default {
  path: "/app/settings",
  name: "settings",
  component: AppSettingsPage,
  redirect: { name: "settings.profile" },
  children: [
    {
      path: "profile",
      name: "settings.profile",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsProfile,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "profile/delete",
      name: "settings.profile.delete.confirm",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsProfile,
        right: AppSettingsProfileDeactivateAccountConfirm,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "security",
      name: "settings.security",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsSecurity,
      },
      meta: {
        type: "settings",
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "orders",
      name: "shopping-cart.orders",
      components: {
        menu: AppSettingsMenu,
        main: AppShoppingCartOrders,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "order/:orderId/notification",
      name: "shopping-cart.orders.order.notification",
      components: {
        menu: AppSettingsMenu,
        main: AppShoppingCartOrder,
        right: AppShoppingCartOrderPurchasedNotification,
      },
      props: {
        main: true,
        right: true,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "order/:orderId",
      name: "shopping-cart.orders.order",
      components: {
        menu: AppSettingsMenu,
        main: AppShoppingCartOrder,
      },
      props: {
        main: true,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "api",
      name: "settings.api",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsAPI,
      },
      meta: {
        middleware: [isUserLoggedIn, isUserVerifiedOrSubscribed],
      },
    },
    {
      path: "api/edit/:tokenId",
      name: "settings.api.edit",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsAPI,
        right: AppSettingsAPIEdit,
      },
      meta: {
        middleware: [isUserLoggedIn, isUserVerifiedOrSubscribed],
      },
    },
    {
      path: "api/new",
      name: "settings.api.new",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsAPI,
        right: AppSettingsAPINew,
      },
      meta: {
        middleware: [isUserLoggedIn, isUserVerifiedOrSubscribed],
      },
    },
    {
      path: "branded-domains",
      name: "settings.brandedDomains",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsBrandedDomains,
      },
      meta: {
        type: "settings",
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "branded-domains/create",
      name: "settings.brandedDomains.createApex",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsBrandedDomains,
        right: CreateApex,
      },
      meta: {
        type: "settings",
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "branded-domains/create-subdomain",
      name: "settings.brandedDomains.createSubdomain",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsBrandedDomains,
        right: CreateSubdomain,
      },
      meta: {
        type: "settings",
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "branded-domains/create-tiny",
      name: "settings.brandedDomains.createTiny",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsBrandedDomains,
        right: CreateTiny,
      },
      meta: {
        type: "settings",
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "branded-domains/verify",
      name: "settings.brandedDomains.verify",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsBrandedDomains,
        right: AppSettingsBrandedDomainsEmailVerify,
      },
      meta: {
        type: "settings",
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "branded-domains/edit/:domain",
      name: "settings.brandedDomains.edit",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsBrandedDomains,
        right: AppSettingsBrandedDomainsEdit,
      },
      meta: {
        type: "settings",
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "pixel-scripts",
      name: "settings.pixelScripts",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsPixelScripts,
      },
      meta: {
        middleware: [isUserLoggedIn, isUserVerifiedOrSubscribed],
      },
    },
    {
      path: "usage",
      name: "settings.usage",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsUsage,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "subscription",
      name: "settings.subscription",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsSubscription,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "subscription/success",
      name: "settings.subscription.success",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsSubscriptionSuccess,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "subscription/cancel",
      name: "settings.subscription.cancel",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsSubscription,
        right: AppSettingsSubscriptionCancel,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "subscription/update/verification",
      name: "settings.subscription.update.verification",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsSubscriptionUpdateVerification,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "payment-method",
      name: "settings.paymentMethod",
      ...billingPageRedirect,
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsPaymentMethod,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "invoices",
      name: "settings.invoices",
      ...billingPageRedirect,
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsInvoices,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "billing",
      name: "settings.billing",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsBilling,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "billing/transactions",
      name: "settings.billing.transactions",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsBillingTransactions,
      },
      meta: {
        middleware: [isUserLoggedIn],
      },
    },
    {
      path: "teams",
      name: "settings.teams",
      components: {
        menu: AppSettingsMenu,
        main: AppSettingsTeams,
      },
      meta: {
        middleware: [isUserLoggedIn, teamsAllowed],
      },
    },
  ],
};
